import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { authAPI } from '../../services/api';
import './ProfilePage.css';

const ProfilePage: React.FC = () => {
  const { user, logout, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'personal' | 'bank' | 'security'>('personal');
  const [editingEmail, setEditingEmail] = useState(false);
  const [emailValue, setEmailValue] = useState(user?.email || '');
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailMessage, setEmailMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    setEmailValue(user?.email || '');
  }, [user?.email]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-ZA', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const extractErrorMessage = (error: any, fallback: string) => {
    const data = error?.response?.data;
    if (data?.message) return data.message;
    if (Array.isArray(data?.details) && data.details[0]?.msg) return data.details[0].msg;
    if (data?.error) return data.error;
    return error?.message || fallback;
  };

  const handleSaveEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailMessage(null);

    const trimmed = emailValue.trim().toLowerCase();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setEmailMessage({ type: 'error', text: 'Please enter a valid email address.' });
      return;
    }

    if (trimmed === (user?.email || '').toLowerCase()) {
      setEditingEmail(false);
      return;
    }

    setEmailSaving(true);
    try {
      await authAPI.updateProfile({ email: trimmed });
      await refreshProfile();
      setEditingEmail(false);
      setEmailMessage({ type: 'success', text: 'Email updated successfully.' });
    } catch (error: any) {
      setEmailMessage({
        type: 'error',
        text: extractErrorMessage(error, 'Failed to update email'),
      });
    } finally {
      setEmailSaving(false);
    }
  };

  const handleCancelEmailEdit = () => {
    setEmailValue(user?.email || '');
    setEditingEmail(false);
    setEmailMessage(null);
  };

  return (
    <div className="profile-page">
      {/* Header */}
      <header className="profile-header">
        <button className="back-btn" onClick={() => navigate('/dashboard')}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Back
        </button>
        <h1>My Profile</h1>
      </header>

      {/* Profile Card */}
      <div className="profile-card">
        <div className="profile-avatar">
          <span>{user?.first_name?.[0]}{user?.last_name?.[0]}</span>
        </div>
        <h2 className="profile-name">{user?.first_name} {user?.last_name}</h2>
        <p className="profile-phone">{user?.phone_number}</p>
        
        <div className="verification-badges">
          <span className={`badge ${user?.is_verified ? 'verified' : 'pending'}`}>
            {user?.is_verified ? '✓ Phone Verified' : '○ Phone Unverified'}
          </span>
          <span className={`badge ${user?.nsfas_verified ? 'verified' : 'pending'}`}>
            {user?.nsfas_verified ? '✓ NSFAS Verified' : '○ NSFAS Pending'}
          </span>
        </div>
        
        <p className="member-since">
          Member since {formatDate(user?.created_at)}
        </p>
      </div>

      {/* Tabs */}
      <div className="profile-tabs">
        <button 
          className={`tab ${activeTab === 'personal' ? 'active' : ''}`}
          onClick={() => setActiveTab('personal')}
        >
          Personal Info
        </button>
        <button 
          className={`tab ${activeTab === 'bank' ? 'active' : ''}`}
          onClick={() => setActiveTab('bank')}
        >
          Bank Details
        </button>
        <button 
          className={`tab ${activeTab === 'security' ? 'active' : ''}`}
          onClick={() => setActiveTab('security')}
        >
          Security
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'personal' && (
          <div className="info-section">
            <div className="info-row">
              <span className="info-label">Full Name</span>
              <span className="info-value">{user?.first_name} {user?.last_name}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Phone Number</span>
              <span className="info-value">{user?.phone_number}</span>
            </div>

            {!editingEmail ? (
              <div className="info-row info-row--email">
                <span className="info-label">Email</span>
                <div className="info-value-with-action">
                  <span className="info-value">{user?.email || 'Not provided'}</span>
                  <button
                    type="button"
                    className="edit-email-btn"
                    onClick={() => {
                      setEmailValue(user?.email || '');
                      setEmailMessage(null);
                      setEditingEmail(true);
                    }}
                  >
                    Edit
                  </button>
                </div>
              </div>
            ) : (
              <form className="email-edit-form" onSubmit={handleSaveEmail}>
                <label className="info-label" htmlFor="profile-email">
                  Email
                </label>
                <input
                  id="profile-email"
                  type="email"
                  className="email-edit-input"
                  value={emailValue}
                  onChange={(e) => setEmailValue(e.target.value)}
                  placeholder="you@example.com"
                  autoComplete="email"
                  disabled={emailSaving}
                  required
                />
                <div className="email-edit-actions">
                  <button
                    type="button"
                    className="email-cancel-btn"
                    onClick={handleCancelEmailEdit}
                    disabled={emailSaving}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="email-save-btn" disabled={emailSaving}>
                    {emailSaving ? 'Saving…' : 'Save email'}
                  </button>
                </div>
              </form>
            )}

            {emailMessage && (
              <div
                className={`profile-inline-message profile-inline-message--${emailMessage.type}`}
                role="status"
              >
                {emailMessage.text}
              </div>
            )}

            <div className="info-row">
              <span className="info-label">SA ID Number</span>
              <span className="info-value">{user?.sa_id_number ? `****${user.sa_id_number.slice(-4)}` : 'Not provided'}</span>
            </div>
          </div>
        )}

        {activeTab === 'bank' && (
          <div className="info-section">
            <div className="info-row">
              <span className="info-label">Bank Name</span>
              <span className="info-value">{user?.bank_name || 'Not provided'}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Account Holder</span>
              <span className="info-value">{user?.account_holder_name || `${user?.first_name} ${user?.last_name}`}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Account Number</span>
              <span className="info-value">
                {user?.bank_account_number ? `****${user.bank_account_number.slice(-4)}` : 'Not provided'}
              </span>
            </div>
            <div className="info-row">
              <span className="info-label">Branch Code</span>
              <span className="info-value">{user?.bank_branch_code || 'Not provided'}</span>
            </div>
            <p className="info-note">
              💡 To update bank details, please contact support.
            </p>
          </div>
        )}

        {activeTab === 'security' && (
          <div className="info-section">
            <div className="security-item">
              <div className="security-info">
                <h4>Phone Verification</h4>
                <p>{user?.is_verified ? 'Your phone number is verified' : 'Please verify your phone number'}</p>
              </div>
              <span className={`status-indicator ${user?.is_verified ? 'verified' : 'pending'}`}>
                {user?.is_verified ? '✓' : '!'}
              </span>
            </div>
            
            <div className="security-item">
              <div className="security-info">
                <h4>Password</h4>
                <p>Last changed: Unknown</p>
              </div>
              <button className="change-btn" disabled>Change</button>
            </div>
            
            <button className="logout-btn" onClick={logout}>
              Log Out
            </button>
          </div>
        )}
      </div>

      {/* Support Section */}
      <div className="support-section">
        <h3>Need Help?</h3>
        <div className="support-options">
          <a href="mailto:info@genlend.co.za" className="support-link">
            <span>✉️</span>
            <span>Email Support</span>
          </a>
          <a href="https://wa.me/27722734008" className="support-link" target="_blank" rel="noopener noreferrer">
            <span>💬</span>
            <span>WhatsApp 072 273 4008</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
